import { Members } from 'pusher-js';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SessionUser } from '~/components/Workflow/TicketReviewerPage/DocumentViewersAndEditors/types';
import { usePusherContext } from '~/contexts/PusherContext';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import * as selectors from '~/redux/selectors';
import { actions } from '~/redux/slices';
import { Uuid } from '~/types/Uuid';

const events = {
  EDIT_EVENT: 'client-edit',
  EDIT_STOP_EVENT: 'client-edit-stop',
};

interface Member {
  id: string;
}

const JOIN_TYPE_MEMBERS_CHECK = 'members_check';
const JOIN_TYPE_EDIT = 'edit';

interface Props {
  /** Supported document editors */
  documentEditor: 'ej2' | 'wopi' | 'sharepoint';
  /** Document version UUID */
  versionId: Uuid;
  /** Predicate to determine if the members (e.g. me) can edit */
  canEdit?: (members: Members) => boolean;
}

/**
 * This hook integrates with Pusher presence channel to track editors on document versions on specific document editors (e.g. ej2, wopi).
 *
 * It returns the `edit` and `stopEdit` callbacks to trigger editing and stop editing events on the appropriate Pusher channels by the `currentUserId`.
 *
 * It binds and listens to the presence channel and keeps editors (managed as a `Set`) in sync with redux store (keyed on `versionId` and `documentEditor`).
 *
 * Pusher note: We use a combination of private and presence channels to track editors. The private channel is used to broadcast edit and stop edit events, and the presence channel is used to track members (without ever being directly subscribed).
 */
export const useTrackDocumentEditors = ({
  canEdit = () => true,
  documentEditor,
  versionId,
}: Props) => {
  const { getPusher, getPresenceChannelMembers } = usePusherContext();
  const pusher = getPusher();
  const [isReady, setIsReady] = useState(false);

  const dispatch = useDispatch();

  const currentUser = useCurrentUser();
  const currentUserId = currentUser.id.toString();

  const editors = useSelector(
    selectors.selectDocumentEditors({
      documentEditor,
      versionId,
    }),
  );

  const addEditor = (id: Uuid) => {
    dispatch(
      actions.addEditor({
        documentEditor,
        editor: id,
        versionId,
      }),
    );
  };

  const removeEditor = (id: Uuid) => {
    dispatch(
      actions.removeEditor({
        documentEditor,
        editor: id,
        versionId,
      }),
    );
  };

  const setEditors = (ids: Array<Uuid>) => {
    dispatch(
      actions.setEditors({
        documentEditor,
        editors: ids,
        versionId,
      }),
    );
  };

  const broadcastChannelName = `private-editors-${documentEditor}-${versionId}`;
  const presenceChannelName = `presence-editors-${documentEditor}-${versionId}`;

  const broadcastChannel = useMemo(() => {
    const channel = pusher.subscribe(broadcastChannelName);
    channel.bind(events.EDIT_EVENT, ({ id }: Member) => addEditor(id));
    channel.bind(events.EDIT_STOP_EVENT, ({ id }: Member) => removeEditor(id));
    return channel;
  }, [broadcastChannelName]);

  // load the initial editors (presence channel members) and cleanup appropriately
  useEffect(() => {
    getPresenceChannelMembers(
      pusher,
      presenceChannelName,
      true,
      JOIN_TYPE_MEMBERS_CHECK,
    )
      .then((members) => {
        // exclude users that are just checking who is in the channel. Only want to return editors that are actually editing.
        const initialEditors = Object.keys(members.members).filter((userId) => {
          const memberInfo = members.members[userId];
          return memberInfo?.join_type !== JOIN_TYPE_MEMBERS_CHECK;
        });
        setEditors(initialEditors);
        setIsReady(true);
      })
      .catch(() => {
        setIsReady(true);
      });

    window.addEventListener('beforeunload', stopEdit);

    return () => {
      stopEdit();
      pusher.unsubscribe(broadcastChannelName);
      pusher.unsubscribe(presenceChannelName);
      window.removeEventListener('beforeunload', stopEdit);
    };
  }, []);

  const edit = async () => {
    const members = await getPresenceChannelMembers(
      pusher,
      presenceChannelName,
      false,
      JOIN_TYPE_EDIT,
    );
    if (canEdit(members)) {
      broadcastChannel.trigger(events.EDIT_EVENT, {
        id: currentUserId,
      });
      addEditor(currentUserId);
    }
  };

  const stopEditSharepointSession = (sessionUsers: Array<SessionUser>) => {
    // multiple users can edit the same document using Microsoft Word for desktop
    // we need to remove all the editors when the session was closed
    sessionUsers.forEach((user) => {
      broadcastChannel.trigger(events.EDIT_STOP_EVENT, {
        id: user.evisortUserId.toString(),
      });
      removeEditor(user.evisortUserId.toString());
    });
    pusher.unsubscribe(presenceChannelName);
  };

  const stopEditSharepoint = () => {
    broadcastChannel.trigger(events.EDIT_STOP_EVENT, {
      id: currentUserId,
    });
    removeEditor(currentUserId);
    pusher.unsubscribe(presenceChannelName);
  };

  const stopEdit = () => {
    broadcastChannel.trigger(events.EDIT_STOP_EVENT, {
      id: currentUserId,
    });
    removeEditor(currentUserId);
    pusher.unsubscribe(presenceChannelName);
  };

  return {
    isReady,
    editors,
    edit,
    stopEdit,
    stopEditSharepointSession,
    stopEditSharepoint,
  };
};
